import type { JournalPost } from '@/types'

import { query } from '@/lib/db'

type JournalRow = {
    id: string
    slug: string
    title: string
    category: string
    excerpt: string | null
    body: string | null
    cover_image: string | null
    cover_image_alt: string | null
    body_image: string | null
    body_image_alt: string | null
    author: string | null
    published_at: string | null
    featured: number
    sort_order: number
}

const SELECT_FIELDS = `
    id, slug, title, category, excerpt, body,
    cover_image, cover_image_alt, body_image, body_image_alt,
    author, published_at, featured, sort_order
`

const toPost = (row: JournalRow): JournalPost => ({
    id: row.id,
    slug: row.slug,
    title: row.title,
    category: row.category,
    excerpt: row.excerpt ?? '',
    body: row.body ?? '',
    coverImage: row.cover_image ?? '/images/barrier-macro.png',
    coverImageAlt: row.cover_image_alt ?? '',
    bodyImage: row.body_image,
    bodyImageAlt: row.body_image_alt,
    author: row.author ?? 'Nuvéa',
    publishedAt: row.published_at ?? ''
})

export const getJournalPosts = async (limit?: number): Promise<JournalPost[]> => {
    const rows = await query<JournalRow>(
        `SELECT ${SELECT_FIELDS} FROM journal_posts ORDER BY sort_order ASC, published_at DESC ${limit ? 'LIMIT ?' : ''}`,
        limit ? [limit] : []
    )

    return rows.map(toPost)
}

export const getJournalPost = async (slug: string): Promise<JournalPost | null> => {
    const rows = await query<JournalRow>(
        `SELECT ${SELECT_FIELDS} FROM journal_posts WHERE slug = ? LIMIT 1`,
        [slug]
    )

    return rows[0] ? toPost(rows[0]) : null
}

export const getJournalSlugs = async (): Promise<string[]> => {
    const rows = await query<{ slug: string }>('SELECT slug FROM journal_posts')
    return rows.map((row) => row.slug)
}

export const getRelatedPosts = async (slug: string, limit = 2): Promise<JournalPost[]> => {
    const rows = await query<JournalRow>(
        `SELECT ${SELECT_FIELDS} FROM journal_posts WHERE slug != ? ORDER BY sort_order ASC LIMIT ?`,
        [slug, limit]
    )

    return rows.map(toPost)
}

/** Renders an ISO date as the short form used across the design, e.g. "Mar 14, 2025". */
export const formatPostDate = (value: string): string => {
    if (!value) return ''

    const [year, month, day] = value.slice(0, 10).split('-').map(Number)

    if (!year || !month || !day) return value

    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ]

    return `${months[month - 1]} ${day}, ${year}`
}

/**
 * Splits the article body at the first pull quote so the in-article image can sit
 * in the same slot the source design uses. Falls back to placing it at the end.
 */
export const splitBodyAtPullQuote = (body: string): [string, string] => {
    const marker = '</h3>'
    const index = body.indexOf(marker)

    if (index === -1) return [body, '']

    const cut = index + marker.length

    return [body.slice(0, cut), body.slice(cut)]
}
